import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { Button } from "../../components/ui/button";
import { formatDateTime } from "../../lib/format";
import { KIND, PAGE_SIZES, PURPOSE, TIER, useForexAdmin } from "./api";
import {
  AccountLink,
  AdminPager,
  Fig,
  FilterBar,
  FilterField,
  OpenedBy,
  UserLink,
  mono,
  muted,
  orDash,
  useListState,
} from "./parts";

const EMPTY = {
  kind: "",
  tier: "",
  purpose: "",
  username: "",
  login: "",
  sort: "created_at",
  order: "desc",
  limit: "50",
};

const SORTS = [
  { value: "created_at", label: "Newest opened" },
  { value: "balance", label: "Balance" },
  { value: "equity", label: "Equity" },
];
const ORDERS = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
];

const COLUMNS = [
  { label: "Login" },
  { label: "Username" },
  { label: "Kind" },
  { label: "Tier" },
  { label: "Purpose" },
  { label: "Group" },
  { label: "Currency" },
  { label: "Balance", align: "right" },
  { label: "Credit", align: "right" },
  { label: "Equity", align: "right" },
  { label: "Margin", align: "right" },
  { label: "Free margin", align: "right" },
  { label: "Margin level", align: "right" },
  { label: "Floating", align: "right" },
  { label: "Realized", align: "right" },
  { label: "Commission", align: "right" },
  { label: "Swap", align: "right" },
  { label: "Opened" },
  { label: "Opened by" },
];

export function AccountRow({ row }) {
  return (
    <tr className="hover:bg-white/[0.02]">
      <Td className={mono}>
        <AccountLink id={row.id}>{row.login}</AccountLink>
      </Td>
      <Td className={mono}>
        <UserLink username={row.username} />
      </Td>
      <Td className="text-xs">{row.kind}</Td>
      <Td className="text-xs">{row.tier}</Td>
      <Td className="whitespace-nowrap text-xs">{row.purpose}</Td>
      <Td className={muted}>{row.group}</Td>
      <Td className="text-xs">{row.currency}</Td>
      <Td align="right"><Fig v={row.balance} /></Td>
      <Td align="right"><Fig v={row.credit} /></Td>
      <Td align="right"><Fig v={row.equity} /></Td>
      <Td align="right"><Fig v={row.margin} /></Td>
      <Td align="right"><Fig v={row.freeMargin} /></Td>
      {/* null here is "no margin used", not a mirror outage. */}
      <Td align="right" className={mono}>{row.marginLevel == null ? orDash(null) : `${Number(row.marginLevel).toFixed(2)}%`}</Td>
      <Td align="right"><Fig v={row.floatingProfit} /></Td>
      <Td align="right"><Fig v={row.realizedProfit} /></Td>
      <Td align="right"><Fig v={row.commission} /></Td>
      <Td align="right"><Fig v={row.swap} /></Td>
      <Td className={muted}>{formatDateTime(row.createdAt)}</Td>
      <Td className="whitespace-nowrap text-xs"><OpenedBy id={row.createdByOperatorId} /></Td>
    </tr>
  );
}

export default function Accounts() {
  const s = useListState(EMPTY);
  const q = useForexAdmin("/accounts/admin", s.params);

  // A balance/equity sort needs the mirror for every account; the default sort
  // still loads (figures null) when it is down.
  const figureSortDown = q.error?.status === 503 && s.applied.sort !== "created_at";
  const fallBack = () => {
    s.setDraft((d) => ({ ...d, sort: "created_at" }));
    s.setApplied({ ...s.applied, sort: "created_at" });
  };

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Trading"
      accent="accounts"
      description="Every trading account with its live figures. n/a means the trading figures could not be read right now, not zero."
      isFetching={q.isFetching}
      onRefresh={() => q.refetch()}
    >
      <FilterBar onApply={s.apply} onReset={s.reset}>
        <FilterField label="Kind" name="kind" options={KIND} draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Tier" name="tier" options={TIER} draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Purpose" name="purpose" options={PURPOSE} draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Username (exact)" name="username" draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="MT5 login" name="login" draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Sort" name="sort" options={SORTS} draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Order" name="order" options={ORDERS} draft={s.draft} setDraft={s.setDraft} />
        <FilterField label="Page size" name="limit" options={PAGE_SIZES} draft={s.draft} setDraft={s.setDraft} />
      </FilterBar>

      {q.error ? (
        <div className="space-y-3">
          <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
          {figureSortDown && (
            <Button size="sm" variant="outline" onClick={fallBack}>
              Sort by newest instead
            </Button>
          )}
        </div>
      ) : (
        <>
          <QueueTable
            columns={COLUMNS}
            isLoading={q.isLoading}
            rows={q.data?.entries}
            renderRow={(row) => <AccountRow key={row.id} row={row} />}
          />
          <AdminPager data={q.data} onChange={s.setPage} label="accounts" />
        </>
      )}
    </QueueLayout>
  );
}
